'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { LiveAuctionCard } from './index';
import { Auction } from '@/lib/types/api';
import { useFavorites } from '@/lib/hooks/useFavorites';

interface InteractiveAuctionSectionProps {
  activeAuctions: Auction[];
  error: string | null;
  isAuthenticated?: boolean; // Server-side'dan gelecek
  locale: string;
}

// Resim URL'ini proxy URL'ine çevir
function getProxyImageUrl(imageUrl: string): string {
  if (!imageUrl) return '';
  
  // example.com URL'lerini proxy URL'ine çevir
  if (imageUrl.includes('example.com')) {
    return imageUrl.replace('https://example.com', '');
  }
  
  return imageUrl;
}

// API verilerini LiveAuctionCard formatına dönüştür
function transformAuctionData(auction: Auction) {
  return {
    id: auction.id,
    slug: auction.slug,
    title: auction.title,
    location: `${auction.districtName}, ${auction.cityName}`,
    startingPrice: auction.startingPrice,
    image: getProxyImageUrl(auction.image || ''), // API'den gelen resmi proxy URL'ine çevir
    isFavorite: auction.isFavorite
  };
}

export default function InteractiveAuctionSection({
  activeAuctions,
  error,
  isAuthenticated = false,
  locale
}: InteractiveAuctionSectionProps) {
  const [favorites, setFavorites] = useState<string[]>([]);
  // Yerel liste: toggle sonrası isFavorite alanını anında yansıtmak için
  const [activeList, setActiveList] = useState(activeAuctions);
  const auctionsT = useTranslations('auctions');
  const { toggleFavorite } = useFavorites();

  const handleFavoriteToggle = async (id: string) => {
    // Mevcut favori durumunu bul
    const auction = activeList.find(a => a.id === id);
    const currentFavoriteStatus = auction?.isFavorite || false;
    
    try {
      // API'ye istek gönder
      await toggleFavorite(id, currentFavoriteStatus);
      
      // Client state'i güncelle
      setFavorites(prev => 
        currentFavoriteStatus
          ? prev.filter(favId => favId !== id)
          : [...prev, id]
      );
      // Aktif listede güncelle
      setActiveList(prev => prev.map(a => a.id === id ? { ...a, isFavorite: !a.isFavorite } : a));
    } catch (error) {
      // Error toggling favorite
    }
  };

  // API'den gelen isFavorite bilgisi ile favorileri senkronize et
  // active listesi değiştiğinde birebir eşitle
  useEffect(() => {
    const initialFavIds = activeAuctions.filter(a => a.isFavorite).map(a => a.id);
    setFavorites(initialFavIds);
    // Prop değişimlerinde yerel listeyi de senkronize et
    setActiveList(activeAuctions);
  }, [activeAuctions]);

  return (
    <section className="py-4 sm:py-6 bg-gray-50">
      <div className="w-full max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6">
        
        {/* Devam Eden Müzayedeler - En fazla 4 adet */}
        <div>
          <div className="flex items-center mb-6">
            <span className="material-icons text-red-600 mr-3" style={{ fontSize: '2.25rem' }}>gavel</span>
            <h3 className="text-2xl sm:text-3xl font-semibold text-gray-900 font-serif">{auctionsT('ongoing')}</h3>
          </div>
          
          {error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-4">
                <span className="material-icons text-4xl">error_outline</span>
              </div>
              <p className="text-gray-600">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Tekrar Dene
              </button>
            </div>
          ) : activeAuctions.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <span className="material-icons text-4xl">inbox</span>
              </div>
              <p className="text-gray-600">Şu anda aktif müzayede bulunmuyor</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-9 auto-rows-fr">
              {activeList.slice(0, 4).map((auction) => {
                const transformedAuction = transformAuctionData(auction);
                return (
                  <LiveAuctionCard
                    key={auction.id}
                    id={transformedAuction.id}
                    slug={transformedAuction.slug}
                    title={transformedAuction.title}
                    location={transformedAuction.location}
                    startingPrice={transformedAuction.startingPrice}
                    image={transformedAuction.image}
                    endDate={auction.endDate}
                    isFavorite={isAuthenticated ? (favorites.includes(auction.id) || auction.isFavorite === true) : auction.isFavorite === true}
                    onFavoriteToggle={isAuthenticated ? handleFavoriteToggle : undefined}
                    isAuthenticated={isAuthenticated}
                  />
                );
              })}
            </div>
          )}
        </div>
        
        {/* Tüm Müzayedeler Butonu */}
        <div className="mt-8 mb-6">
          <div className="relative bg-transparent rounded-2xl p-2 lg:p-4 overflow-hidden">
            {/* Arka plan ikonu */}
            <div className="absolute top-4 right-4 opacity-10">
              <span className="material-icons text-red-600" style={{ fontSize: '8rem' }}>gavel</span>
            </div>
            <div className="absolute bottom-4 left-4 opacity-10">
              <span className="material-icons text-red-600" style={{ fontSize: '6rem' }}>home_work</span>
            </div>
            
            <div className="relative z-10 text-center">
              <p className="text-gray-700 text-lg mb-6 max-w-2xl mx-auto font-medium">
                {auctionsT('viewAllDescription')}
              </p>
              <Link href={`/${locale}/muzayedeler`} className="inline-block">
                <button className="px-8 py-4 bg-red-600 text-white rounded-full font-semibold text-lg hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200">
                  {auctionsT('viewAll')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
